import {
  SkyBadge,
  SkyButton,
  SkyDataGrid,
  SkyDateRangePicker,
  SkyDropdown,
  SkyInput,
  SkyMessageBar,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import type { SkyDataGridColumn } from "@skypointcloud/skypoint-ui";
import { useId, useMemo, useState, type ReactNode } from "react";
import { AUDIT_ACTIONS } from "@shared/audit/actions";
import { unreadableInstancesWarning, type TenantAuditEventView } from "@shared/types/audit";
import { ErrorState, LoadingRows } from "@/components/access/ScreenStates";
import { ACTOR_HELP, toIsoEnd, toIsoStart } from "@/components/audit/AuditFilters";
import { TopbarActionsPortal } from "@/components/navigation/TopbarActionsPortal";
import {
  tenantAuditExportPath,
  useAuditExport,
  useCursorStack,
  useTenantAuditEvents,
  type TenantAuditFilters,
} from "@/hooks/use-audit";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { useApiErrorNotice } from "@/lib/feedback/useApiErrorNotice";
import { ChevronLeftIcon, ChevronRightIcon, DownloadIcon } from "@/lib/icons";
import { formatCalendarDay, formatInstant } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { usePermissions } from "@/hooks/use-permissions";
import {
  auditActionLabelFor,
  auditActionOptionText,
  auditEntityTypeLabel,
  auditFamilyOptionText,
} from "./actionLabels";
import { byCodeUnit } from "@shared/sort";
import { ZoneCaption } from "@/components/shell/ZoneCaption";
import { RefreshErrorBar } from "@/components/states/RefreshErrorBar";

/**
 * The tenant's whole audit trail (product gaps row 9; owner decision 2026-09-28): the platform
 * rows about the tenant (grants made on the tenant screens, instance create and disable,
 * destination changes, whoever made them, Skypoint staff included) and every instance's own
 * trail, merged newest first by the server.
 *
 * A row carries identifiers only (SEC-17), so there is no change drawer here: a change's detail
 * is on the instance trail, whose tab is beside this one. The actor is shown by address, joined
 * by the server when the page is read; the exported file masks it (SEC-13).
 *
 * Nothing here sorts: the order is the server's, and the cursor walks it.
 */

/**
 * Every action family (`access`, `run`, ...) as a prefix, then every action in the grid's words
 * (walk of 9a9d586, D-c: `customer.create` and `transformation.create` read as keys here while the
 * grid read "Tenant set up"), a historical one (or a family of them) with "(history)"; the value
 * is the stored action.
 * The family `customer.*` is left out: the word Customer is not in the trail, and its two actions
 * are offered by their words.
 */
const ACTION_OPTIONS = [
  { value: "", text: "Any action" },
  ...[...new Set(AUDIT_ACTIONS.map((action) => action.split(".")[0] ?? action))]
    .filter((family) => family !== "customer")
    .sort(byCodeUnit)
    .map((family) => ({ value: `${family}.`, text: auditFamilyOptionText(family, AUDIT_ACTIONS) })),
  ...AUDIT_ACTIONS.map((action) => ({ value: action, text: auditActionOptionText(action) })),
];

/** The floor under the grid's percentage widths; below it the grid scrolls in its own region. */
const TENANT_AUDIT_GRID_MIN_WIDTH = "[&_table]:min-w-[960px]";

function TenantAuditGrid({
  rows,
  emptyMessage,
}: {
  rows: TenantAuditEventView[];
  emptyMessage: string;
}) {
  const zone = useDisplayTz();
  const columns: SkyDataGridColumn<TenantAuditEventView>[] = [
    {
      key: "occurredAt",
      header: "Occurred at",
      width: "15%",
      sortable: false,
      renderCell: (_value, row) => (
        <SkyText size={200} font="numeric">
          {formatInstant(row.occurredAt, zone, "seconds")}
        </SkyText>
      ),
    },
    {
      key: "originLabel",
      header: "Where",
      width: "14%",
      sortable: false,
      renderCell: (_value, row) => (
        <div className="flex min-w-0 flex-col">
          <SkyText size={200}>{row.originLabel}</SkyText>
          <SkyText size={200} color="muted">
            {row.origin === "tenant" ? "Tenant" : "Instance"}
          </SkyText>
        </div>
      ),
    },
    {
      key: "actorEmail",
      header: "Actor",
      width: "24%",
      sortable: false,
      renderCell: (_value, row) => (
        <div className="flex min-w-0 flex-col">
          {/* An address has no spaces, so it alone breaks anywhere rather than running into
              the next column. A system actor has no address; its type names it. */}
          <SkyText size={200} className="break-all">
            {row.actorEmail ?? row.actorRef}
          </SkyText>
          <SkyText size={200} color="muted">
            {row.actorType}
          </SkyText>
        </div>
      ),
    },
    {
      key: "action",
      header: "Action",
      width: "19%",
      sortable: false,
      renderCell: (_value, row) => (
        <div className="flex flex-wrap items-center gap-2">
          {/* Round 7 T-36: every action reads in words, here and in the Action filter
              (`AUDIT_ACTION_LABELS`); the machine name stays in the CSV. An action outside
              the closed set (a row newer than this build) shows its machine name. */}
          {auditActionLabelFor(row.action, row.failed === true) ? (
            <SkyText size={200} block testId={`tenant-audit-action-label-${row.id}`}>
              {auditActionLabelFor(row.action, row.failed === true)}
            </SkyText>
          ) : (
            <SkyText size={200} font="numeric">
              {row.action}
            </SkyText>
          )}
          {row.breakGlass ? (
            <SkyBadge appearance="tint" color="danger" testId={`tenant-break-glass-${row.id}`}>
              Break-glass
            </SkyBadge>
          ) : null}
          {row.failed ? (
            <SkyBadge appearance="tint" color="warning" testId={`tenant-failed-${row.id}`}>
              Failed attempt
            </SkyBadge>
          ) : null}
        </div>
      ),
    },
    {
      key: "entityType",
      header: "Entity",
      width: "18%",
      sortable: false,
      renderCell: (_value, row) => (
        <div className="flex min-w-0 flex-col">
          <SkyText size={200}>{auditEntityTypeLabel(row.action, row.entityType)}</SkyText>
          {row.entityLabel ? (
            <SkyText size={200} color="muted" className="break-all">
              {row.entityLabel}
            </SkyText>
          ) : null}
        </div>
      ),
    },
    {
      key: "correlationId",
      header: "Reference",
      width: "10%",
      sortable: false,
      // A reference is one unbroken token: a block that truncates keeps it inside its column
      // (an inline element ignores `truncate`, and at 1440 px it ran past the grid's right
      // edge), and the title holds the whole reference for hover.
      renderCell: (_value, row) => (
        <SkyText
          size={200}
          font="numeric"
          block
          truncate
          title={row.correlationId}
          testId={`tenant-audit-reference-${row.id}`}
        >
          {row.correlationId}
        </SkyText>
      ),
    },
  ];

  return (
    <>
      <SkyDataGrid<TenantAuditEventView>
        className={TENANT_AUDIT_GRID_MIN_WIDTH}
        data={rows}
        columns={columns}
        getRowId={(row) => row.id}
        ariaLabel="Tenant audit trail"
        testId="tenant-audit-grid"
        emptyMessage={emptyMessage}
      />
      {/* B-51 (round 9): the trail's times are in the reader's zone, said once. */}
      {rows.length > 0 ? <ZoneCaption testId="tenant-audit-zone-caption" className="mt-2" /> : null}
    </>
  );
}

/** The line under the grid: the page's count, what to widen, or nothing for a new trail. */
function pageLine(count: number, trulyEmpty: boolean): string | null {
  if (count > 0) return `Showing ${count} events on this cursor page.`;
  return trulyEmpty ? null : "Widen the date range or clear a filter.";
}

export function TenantAuditTrail({
  tenantId,
  tenantName,
  instances,
}: {
  tenantId: string;
  tenantName: string;
  /** The tenant's instances the viewer can see, for the Where filter. */
  instances: ReadonlyArray<{ id: string; name: string }>;
}) {
  const notice = useNotice();
  const showError = useApiErrorNotice();
  const zone = useDisplayTz();
  // #1113: the export has its own key, `tenant.audit.export`, as the instance trail's does.
  const canExport = usePermissions().can("tenant.audit.export");
  const [filters, setFilters] = useState<TenantAuditFilters>({});
  const cursor = useCursorStack();
  const events = useTenantAuditEvents(tenantId, filters, cursor.cursor, true);
  const { exportCsv, isExporting } = useAuditExport();
  const actorHelpId = useId();
  // Walk W19: no row on the first page with no filter set is a trail where nothing has happened
  // yet, said once by the grid; with a filter, the grid says nothing matches and the line under
  // it says what to widen.
  const anyFilter = Object.values(filters).some((value) => value !== undefined && value !== "");
  const trulyEmpty = events.rows.length === 0 && !anyFilter && !cursor.canGoBack;

  const originOptions = useMemo(
    () => [
      { value: "", text: "Tenant and every instance" },
      { value: "tenant", text: "Tenant only" },
      ...[...instances]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((instance) => ({ value: instance.id, text: instance.name })),
    ],
    [instances],
  );

  /** Every filter change starts a new walk; keeping the cursor would page the old query. */
  function apply(next: TenantAuditFilters) {
    setFilters(next);
    cursor.reset();
  }

  async function handleExport() {
    try {
      const filename = await exportCsv(tenantAuditExportPath(tenantId, filters), filters.actor);
      notice.success(
        `${filename}. The events of ${tenantName} that match these filters, the newest 10,000 at most.`,
        {
          title: "CSV downloaded",
        },
      );
    } catch (cause) {
      showError(cause, "The export could not be produced.");
    }
  }

  function renderEvents(): ReactNode {
    if (events.isPending) {
      return <LoadingRows rows={6} columns={6} />;
    }
    // #1398: only a failed first read replaces the trail; a failed re-read keeps the rows.
    if (events.isError && events.data === undefined) {
      return (
        <ErrorState
          title="The tenant audit trail did not load"
          error={events.error}
          onRetry={() => void events.refetch()}
        />
      );
    }
    return (
      <>
        {events.isError ? (
          <RefreshErrorBar
            things="events"
            error={events.error}
            onRetry={() => void events.refetch()}
            empty={events.rows.length === 0}
            testId="tenant-audit-refresh-error"
          />
        ) : null}
        {events.unreadableInstances.length > 0 ? (
          // Not an error: the rows below are every other store's, complete. The server names
          // the stores it could not read (`meta.unreadableInstances`); never its error text.
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            testId="tenant-audit-unreadable"
            message={unreadableInstancesWarning(
              events.unreadableInstances,
              formatCalendarDay,
              // The mark is an instant: its day in the person's display zone.
              (iso) => formatInstant(iso, zone, "day"),
            )}
          />
        ) : null}
        <TenantAuditGrid
          rows={events.rows}
          emptyMessage={
            trulyEmpty
              ? "Nothing has happened in this tenant yet."
              : "No events match these filters."
          }
        />
        <div className="flex items-center gap-3">
          <SkyText as="p" size={200} color="muted">
            {pageLine(events.rows.length, trulyEmpty)}
          </SkyText>
          <span className="ml-auto flex items-center gap-2">
            <SkyButton
              appearance="subtle"
              size="small"
              icon={ChevronLeftIcon}
              testId="tenant-audit-previous"
              disabled={!cursor.canGoBack}
              onClick={cursor.back}
            >
              Previous
            </SkyButton>
            <SkyButton
              appearance="secondary"
              size="small"
              icon={ChevronRightIcon}
              testId="tenant-audit-next"
              disabled={!events.nextCursor}
              onClick={() => events.nextCursor && cursor.next(events.nextCursor)}
            >
              Next
            </SkyButton>
          </span>
        </div>
      </>
    );
  }

  return (
    <div className="space-y-4" data-testid="tenant-audit">
      {canExport ? (
        <TopbarActionsPortal>
          <SkyButton
            appearance="secondary"
            icon={DownloadIcon}
            loading={isExporting}
            testId="export-csv"
            onClick={() => void handleExport()}
          >
            Export CSV
          </SkyButton>
        </TopbarActionsPortal>
      ) : null}

      <SkyText as="p" size={200} color="muted">
        Everything that happened in {tenantName}: access granted on the tenant, changes made by
        platform administrators, and every instance&apos;s own trail, newest first.
      </SkyText>

      <div className="flex flex-wrap items-end gap-3" data-testid="tenant-audit-filters">
        <div className="w-64">
          <SkyInput
            label="Actor"
            type="search"
            placeholder="Email address or user id"
            value={filters.actor ?? ""}
            testId="tenant-audit-actor"
            // The help is the line under the row (walk W20: a hint here pushed Actor off the
            // row's baseline), and still this field's description.
            aria-describedby={actorHelpId}
            onChange={(event) => apply({ ...filters, actor: event.target.value })}
          />
        </div>
        {/* Round 7 T-36: w-56 cut "Tenant and every instance", the default choice. */}
        <div className="w-72">
          <SkyDropdown
            label="Where"
            testId="tenant-audit-origin"
            selectedOptions={[filters.origin ?? ""]}
            options={originOptions}
            onOptionSelect={(values) => apply({ ...filters, origin: values[0] ?? "" })}
          />
        </div>
        <div className="w-64">
          <SkyDropdown
            label="Action"
            testId="tenant-audit-action"
            selectedOptions={[filters.actionPrefix ?? ""]}
            listboxMaxHeight="320px"
            options={ACTION_OPTIONS}
            onOptionSelect={(values) => apply({ ...filters, actionPrefix: values[0] ?? "" })}
          />
        </div>
        <div className="w-72">
          <SkyDateRangePicker
            label="Occurred between"
            testId="tenant-audit-date-range"
            onSelectDateRange={(range) =>
              apply({
                ...filters,
                from: toIsoStart(range.startDate, zone),
                to: toIsoEnd(range.endDate, zone),
              })
            }
          />
        </div>
        <SkyButton
          appearance="subtle"
          size="small"
          testId="tenant-audit-clear-filters"
          onClick={() => apply({})}
        >
          Clear filters
        </SkyButton>
      </div>
      <SkyText id={actorHelpId} size={200} color="muted" block testId="tenant-audit-actor-help">
        {ACTOR_HELP}
      </SkyText>

      {renderEvents()}
    </div>
  );
}
